import { SessionContext } from "@/context/SessionContext";
import ConductionAPI, { SubmissionTokenForm } from "@/javascript/conduction-api";
import { Classroom, User } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Button, Flex, Loader, Modal, NumberInput, Stack, Text } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useState } from "react";

interface SubmissionTokenCreatorModalProps {
	classroom: Classroom | undefined;
	user: User | undefined;
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	onCreate?: () => void; // Optional callback to refresh the classroom list after creation
}

const SubmissionTokenCreatorModal: React.FC<SubmissionTokenCreatorModalProps> = ({ classroom, user, showModal, setShowModal, onCreate }) => {
	const [loading, setLoading] = useState<boolean>(false);

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error("AdminClassroomDeletionForm must be a child of SessionContext.");
	}
	const token = session.token;
	if (!token) return null;

	const form = useForm({
		initialValues: {
			credits: 0,
			start: new Date(),
			expiration: new Date()
		},
		validate: {
			credits: (value) =>
				value <= 0
					? "Credits must be greater than zero."
					: null,
			start: (_) => null,
			expiration: (value, values) => {
				// Create copies of the dates to normalize their times.
				const normalizedStart = new Date(values.start);
				normalizedStart.setHours(0, 0, 0, 0); // 12:00 AM

				const normalizedExpiration = new Date(value);
				normalizedExpiration.setHours(23, 59, 59, 999); // 11:59 PM

				// Now, if the expiration is before the start day, return an error.
				return normalizedExpiration < normalizedStart
					? "Expiration date must be the same day or later than the start date."
					: null;
			}
		},
	});

	const handleSubmit = async () => {
        let id: string | undefined;
        let type: "classroom" | "user" | undefined;

        if (classroom === undefined && user !== undefined) {
            id = user.id;
            type = "user";
        }

        if (classroom !== undefined && user === undefined) {
            id = classroom.id;
            type = "classroom";
        }

        if (id === undefined || type === undefined) {
            return;
        }

		setLoading(true);

		// Get the selected local dates
		const localStart = new Date(form.values.start);
		const localExpiration = new Date(form.values.expiration);

		// Normalize times: start at midnight and expiration at end-of-day
		localStart.setHours(0, 0, 0, 0);
		localExpiration.setHours(23, 59, 59, 999);

		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Creating credits...",
			undefined,
			"blue",
			"loading"
		);

		const submissionTokenForm: SubmissionTokenForm = {
			credits: form.values.credits,
			start: localStart,
			expiration: localExpiration
		};

		try {
			// Call the API to delete the classroom.
			await ConductionAPI.addSubmissionToken(type, id, submissionTokenForm, token);

			Utils.updateNotification(
				notificationId,
				"Credits added!",
				undefined,
				"green",
				<IconCheck size={16} />
			);

			if (onCreate) {
				onCreate();
			}
			setShowModal(false);
		} catch (error) {
			console.error("Error creating tokens", error);
			Utils.updateNotification(
				notificationId,
				"Error Occurred",
				undefined,
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		} finally {
			setLoading(false);
		}
	};

	if (user === undefined && classroom === undefined) {
		// Neither user nor classroom are defined
		throw new Error("User or classroom must be provided.");
	}

	if (user !== undefined && classroom !== undefined) {
		// Both user and classroom are defined
		throw new Error("User and classroom cannot both be provided.")
	}

	return (
		<Modal
			opened={showModal}
			onClose={() => setShowModal(false)}
			title="Create Submission Credits"
			centered
			size="xl"
		>
			{loading ? (
				<Loader height="100%" />
			) : classroom === null ? (
				<Text>{user !== undefined ? "User" : "Classroom"} data did not load correctly.</Text>
			) : (
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Stack>
						<Text>
							Adding submission credits to {user !== undefined ? `${user.username}'s personal account:` : `${classroom!.name} classroom:`}
						</Text>
						<NumberInput
							label="Credits"
							required
							{...form.getInputProps("credits")}
						/>
						<Flex
							direction={"row"}
							justify={"space-between"}
						>
							<Stack
							>
								<Text>Start Date</Text>
								<DatePicker
									{...form.getInputProps("start")}
								/>
							</Stack>
							<Stack>
								<Text>Expiration Date</Text>
								<DatePicker
									{...form.getInputProps("expiration")}
								/>
							</Stack>
						</Flex>
						<Text c="red">{form.errors.expiration}</Text>
						<Button type="submit" radius="xl" size="sm" loading={form.submitting} color="red">
							Create and Add Credits
						</Button>
					</Stack>
				</form>
			)}
		</Modal>
	);
};

export default SubmissionTokenCreatorModal;
